import { useEffect } from 'react';
import { DEFAULT_DOCUMENT_TITLE } from '../hooks/useDocumentTitle';

interface ShareMetaTagsProps {
  username: string;
  score: number;
  rank: number;
  shareUrl: string;
  percentile?: number;
}

const ShareMetaTags: React.FC<ShareMetaTagsProps> = ({
  username,
  score,
  rank,
  shareUrl,
  percentile
}) => {
  useEffect(() => {
    // Update or create meta tags
    const setMetaTag = (property: string, content: string, isProperty = true) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement;

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    const title = `${username} scored ${score} pts (#${rank}) on Cognidaily`;
    
    // Format percentile for description (percentile represents % of players below this score)
    const formatPercentileForMeta = (p: number): string => {
      const clamped = Math.max(0, Math.min(100, p));
      if (clamped >= 99) return 'Top 1%';
      if (clamped >= 95) return 'Top 5%';
      if (clamped >= 90) return 'Top 10%';
      if (clamped >= 75) return 'Top 25%';
      if (clamped >= 50) return 'Top 50%';
      return `Top ${Math.max(1, Math.ceil(clamped))}%`;
    };
    
    const description = percentile !== undefined && percentile !== null
      ? `${username} just scored ${score} points and ranked #${rank} (${formatPercentileForMeta(percentile)}) on Cognidaily's daily quiz!`
      : `${username} just scored ${score} points and ranked #${rank} on Cognidaily's daily quiz!`;

    // Update document title
    document.title = title;

    // Basic meta tags
    setMetaTag('description', description, false);

    // Build full image URL
    const imageUrl = `https://cognidaily.io/og-image.png`;

    // Open Graph tags
    setMetaTag('og:title', title);
    setMetaTag('og:description', description);
    setMetaTag('og:url', shareUrl);
    setMetaTag('og:type', 'website');
    setMetaTag('og:site_name', 'Cognidaily');
    setMetaTag('og:image', imageUrl);
    setMetaTag('og:image:width', '1200');
    setMetaTag('og:image:height', '630');
    setMetaTag('og:image:alt', `${username}'s Cognidaily score: ${score} points, Rank #${rank}`);

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image', false);
    setMetaTag('twitter:title', title, false);
    setMetaTag('twitter:description', description, false);
    setMetaTag('twitter:url', shareUrl, false);
    setMetaTag('twitter:image', imageUrl, false);
    setMetaTag('twitter:image:alt', `${username}'s Cognidaily score`, false);

    // Cleanup function (optional, but good practice)
    return () => {
      // Reset to default if needed
      document.title = DEFAULT_DOCUMENT_TITLE;
    };
  }, [username, score, rank, shareUrl, percentile]);

  return null; // This component doesn't render anything
};

export default ShareMetaTags;




